import { Action, createReducer, on } from '@ngrx/store';
import * as ConfigurationActions from '@actions/configuration.actions';
import { CustomerModel } from '@models/configuration/customer.model';
import { PolicyModel } from '@models/configuration/policy.model';
import { CertificateModel } from '@models/configuration/certificate.model';
import { QueryModel } from '@models/configuration/query.model';

import {
    CustomerActions,
    PolicyActions,
    CertificateActions,
    QueryActions,
    CertificateTypeActions,
    RoleActions,
    AreaActions,
    GeneralConfigActions,
    CertificateHistoricalActions,
} from '@actions/configuration/index';
import { CertificateTypeModel } from '@models/configuration/certificate-type.model';
import { RoleModel } from '@models/configuration/role.model';
import { AreaModel } from '@models/configuration/area.model';
import { CertificateHistoricalModel } from '@models/configuration/certificate-historical.model';
import { GeneralConfigModel } from '@models/configuration/general-config.model';

export interface State {
    Customers: CustomerModel[];
    CustomersAuthorization: CustomerModel[];
    CurrentCustomer: CustomerModel;
    Policies: PolicyModel[];
    CurrentPolicy: PolicyModel;
    Certificates: CertificateModel[];
    CertificateStatus: CertificateModel[];
    CertificatesByState: CertificateModel[];
    CertificatesFile: CertificateModel[];
    CurrentCertificate: CertificateModel;
    CertificateHistoricals: CertificateHistoricalModel[];
    CurrentCertificateHistorical: CertificateHistoricalModel;
    CertificateTypes: CertificateTypeModel[];
    CurrentCertificateType: CertificateTypeModel;
    Queries: QueryModel[];
    CurrentQuery: QueryModel;
    QueryOne: QueryModel[];
    Roles: RoleModel[];
    CurrentRole: RoleModel;
    Areas: AreaModel[];
    CurrentArea: AreaModel;
    GeneralConfigs: GeneralConfigModel[];
    CurrentGeneralConfig: GeneralConfigModel;
    Error: string | null;
    Loading: boolean;
    LoadingPolicy: boolean;
    LoadingCustomer: boolean;
    LoadingArea: boolean;
    LoadingGeneralConfig: boolean;
    Message: string | null;
}

export const initialState: State = {
    Customers: [],
    CustomersAuthorization: [],
    CurrentCustomer: null,
    Policies: [],
    CurrentPolicy: null,
    Certificates: [],
    CertificateStatus: [],
    CertificatesByState: [],
    CertificatesFile: [],
    CurrentCertificate: null,
    CertificateHistoricals: [],
    CurrentCertificateHistorical: null,
    CertificateTypes: [],
    CurrentCertificateType: null,
    Queries: [],
    CurrentQuery: null,
    QueryOne: null,
    Roles: [],
    CurrentRole: null,
    Areas: [],
    CurrentArea: null,
    GeneralConfigs: [],
    CurrentGeneralConfig: null,
    Error: null,
    Loading: false,
    LoadingPolicy: false,
    LoadingCustomer: false,
    LoadingArea: false,
    LoadingGeneralConfig: false,
    Message: null
};

const configurationReducer = createReducer(
    initialState,

     //---------------------------Policy-----------------------------------------
    //--------------------------------------------------------------------------
    on(PolicyActions.searchPolicies, (state) => searchFlags(state)),
    on(PolicyActions.searchPoliciesSuccess, (state, { Policies }) => ({ ...state, Loading: false, Policies, LoadingPolicy:false })),
    on(PolicyActions.createPolicy, (state) => updateFlags(state)),
    on(PolicyActions.createPolicySuccess, (state, { Policy }) => ({
        ...state,
        Loading: false,
        Message: "Política creada correctamente...",
        Policies: [...state.Policies, Policy],
        CurrentPolicy: null,
    })),
    on(PolicyActions.updatePolicy, (state) => updateFlags(state)),
    on(PolicyActions.updatePolicySuccess, (state, { Policy }) => ({
        ...state,
        Loading: false,
        Message: "Política actualizada correctamente...",
        Policies: [...state.Policies, Policy],
        CurrentPolicy: Policy,
    })),
    on(PolicyActions.deletePolicy, (state) => deleteFlags(state)),
    on(PolicyActions.deletePolicySuccess, (state, { Policy }) => ({
        ...state,
        Loading: false,
        Message: "Política eliminada correctamente...",
        Policies: [...state.Policies],
        CurrentPolicy: null,
    })),
    on(PolicyActions.selectPolicy, (state, { Policy }) => ({
        ...state,
        CurrentPolicy: Policy
    })),
    on(PolicyActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
            LoadingPolicy: false,
        };
    }),

    //---------------------------Area-----------------------------------------
    //----------------------------------------------------------------------------
    on(AreaActions.searchAreas, (state) => searchFlags(state)),
    on(AreaActions.searchAreasSuccess, (state, { Areas }) => ({ ...state, Loading: false, LoadingArea: false,Areas })),
    on(AreaActions.createArea, (state) => updateFlags(state)),
    on(AreaActions.createAreaSuccess, (state, { Area }) => ({
        ...state,
        Loading: false,
        Message: "Area creada correctamente...",
        Areas: [...state.Areas, Area],
        CurrentArea: null,
    })),
    on(AreaActions.updateArea, (state) => updateFlags(state)),
    on(AreaActions.updateAreaSuccess, (state, { Area }) => ({
        ...state,
        Loading: false,
        Message: "Area actualizada correctamente...",
        Areas: [...state.Areas, Area],
        CurrentArea: Area,
    })),
    on(AreaActions.deleteArea, (state) => deleteFlags(state)),
    on(AreaActions.deleteAreaSuccess, (state, { Area }) => ({
            ...state,
            Loading: false,
            Message: "Area eliminada correctamente...",
            Areas: [...state.Areas],
            CurrentArea: null,
    })),
    on(AreaActions.selectArea, (state, { Area }) => ({
        ...state,
        CurrentArea: Area
    })),
    on(AreaActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
        };
    }),

    //---------------------------GeneralConfig-----------------------------------------
    //----------------------------------------------------------------------------
    on(GeneralConfigActions.searchGeneralConfigs, (state) => searchFlags(state)),
    on(GeneralConfigActions.searchGeneralConfigsSuccess, (state, { GeneralConfigs }) => ({ ...state, Loading: false, LoadingGeneralConfig: false,GeneralConfigs })),
    on(GeneralConfigActions.createGeneralConfig, (state) => updateFlags(state)),
    on(GeneralConfigActions.createGeneralConfigSuccess, (state, { GeneralConfig }) => ({
        ...state,
        Loading: false,
        Message: "GeneralConfig creada correctamente...",
        GeneralConfigs: [...state.GeneralConfigs, GeneralConfig],
        CurrentGeneralConfig: null,
    })),
    on(GeneralConfigActions.updateGeneralConfig, (state) => updateFlags(state)),
    on(GeneralConfigActions.updateGeneralConfigSuccess, (state, { GeneralConfig }) => ({
        ...state,
        Loading: false,
        Message: "GeneralConfig actualizada correctamente...",
        GeneralConfigs: [...state.GeneralConfigs, GeneralConfig],
        CurrentGeneralConfig: GeneralConfig,
    })),
    on(GeneralConfigActions.deleteGeneralConfig, (state) => deleteFlags(state)),
    on(GeneralConfigActions.deleteGeneralConfigSuccess, (state, { GeneralConfig }) => ({
            ...state,
            Loading: false,
            Message: "GeneralConfig eliminada correctamente...",
            GeneralConfigs: [...state.GeneralConfigs],
            CurrentGeneralConfig: null,
    })),
    on(GeneralConfigActions.selectGeneralConfig, (state, { GeneralConfig }) => ({
        ...state,
        CurrentGeneralConfig: GeneralConfig
    })),
    on(GeneralConfigActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
        };
    }),

    //---------------------------Customer-----------------------------------------
    //----------------------------------------------------------------------------
    on(CustomerActions.searchCustomers, (state) => searchFlags(state)),
    on(CustomerActions.searchCustomersSuccess, (state, { Customers }) => ({ ...state, Loading: false, LoadingCustomer: false, Customers })),
    on(CustomerActions.searchCustomersAuthorization, (state) => searchFlags(state)),
    on(CustomerActions.searchCustomersAuthorizationSuccess, (state, { CustomersAuthorization }) => ({ ...state, Loading: false, CustomersAuthorization })),
    on(CustomerActions.createCustomer, (state) => updateFlags(state)),
    on(CustomerActions.createCustomerSuccess, (state, { Customer }) => ({
        ...state,
        Loading: false,
        Message: "Cliente creado correctamente...",
        Customers: [...state.Customers, Customer],
        CurrentCustomer: null,
    })),
    on(CustomerActions.updateCustomer, (state) => updateFlags(state)),
    on(CustomerActions.updateCustomerSuccess, (state, { Customer }) => ({
        ...state,
        Loading: false,
        Message: "Cliente actualizado correctamente...",
        Customers: [...state.Customers, Customer],
        CurrentCustomer: Customer,
    })),
    on(CustomerActions.deleteCustomer, (state) => deleteFlags(state)),
    on(CustomerActions.deleteCustomerSuccess, (state, { Customer }) => ({
            ...state,
            Loading: false,
            Message: "Cliente eliminado correctamente...",
            Customers: [...state.Customers],
            CurrentCustomer: null,
    })),
    on(CustomerActions.selectCustomer, (state, { Customer }) => ({
        ...state,
        CurrentCustomer: Customer
    })),
    on(CustomerActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
            LoadingCustomer: false,
        };
    }),

    on(CustomerActions.cleanCustomers, (state, { Customers }) => ({ ...state, Customers: [] })),

    //---------------------------CertificateType-----------------------------------------
    //--------------------------------------------------------------------------
    on(CertificateTypeActions.searchCertificateTypes, (state) => searchFlags(state)),
    on(CertificateTypeActions.searchCertificateTypesSuccess, (state, { CertificateTypes }) => ({ ...state, Loading: false, CertificateTypes })),
    on(CertificateTypeActions.createCertificateType, (state) => updateFlags(state)),
    on(CertificateTypeActions.createCertificateTypeSuccess, (state, { CertificateType }) => ({
        ...state,
        Loading: false,
        Message: "Tipo de certificado creada correctamente...",
        CertificateTypes: [...state.CertificateTypes, CertificateType],
        CurrentCertificateType: null,
    })),
    on(CertificateTypeActions.updateCertificateType, (state) => updateFlags(state)),
    on(CertificateTypeActions.updateCertificateTypeSuccess, (state, { CertificateType }) => ({
        ...state,
        Loading: false,
        Message: "Tipo de certificado actualizada correctamente...",
        CertificateTypes: [...state.CertificateTypes, CertificateType],
        CurrentCertificateType: CertificateType,
    })),
    on(CertificateTypeActions.deleteCertificateType, (state) => deleteFlags(state)),
    on(CertificateTypeActions.deleteCertificateTypeSuccess, (state, { CertificateType }) => ({
        ...state,
        Loading: false,
        Message: "Tipo de certificado eliminada correctamente...",
        CertificateTypes: [...state.CertificateTypes],
        CurrentCertificateType: null,
    })),
    on(CertificateTypeActions.selectCertificateType, (state, { CertificateType }) => ({
        ...state,
        CurrentCertificateType: CertificateType
    })),
    on(CertificateTypeActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
        };
    }),

    //---------------------------Certificate-----------------------------------------
    //--------------------------------------------------------------------------
    on(CertificateActions.searchCertificates, (state) => searchFlags(state)),
    on(CertificateActions.searchCertificatesSuccess, (state, { Certificates }) => ({ ...state, Loading: false, Certificates })),
    on(CertificateActions.searchCertificateStatus, (state) => searchFlags(state)),
    on(CertificateActions.searchCertificateStatusSuccess, (state, { CertificateStatus }) => ({ ...state, Loading: false, CertificateStatus })),
    on(CertificateActions.searchCertificatesState, (state) => searchFlags(state)),
    on(CertificateActions.searchCertificatesStateSuccess, (state, { CertificatesByState }) => ({ ...state, Loading: false, CertificatesByState })),
    on(CertificateActions.searchCertificatesFile, (state) => searchFlags(state)),
    on(CertificateActions.searchCertificatesFileSuccess, (state, { CertificatesFile }) => ({ ...state, Loading: false, CertificatesFile })),
    on(CertificateActions.createCertificate, (state) => updateFlags(state)),
    on(CertificateActions.createCertificateSuccess, (state, { Certificate }) => ({
        ...state,
        Loading: false,
        Message: "Certificado creado correctamente...",
        Certificates: [...state.Certificates, Certificate],
        CurrentCertificate: null,
    })),
    on(CertificateActions.updateCertificate, (state) => updateFlags(state)),
    on(CertificateActions.updateCertificateSuccess, (state, { Certificate }) => ({
        ...state,
        Loading: false,
        Message: "Certificado actualizado correctamente...",
        Certificates: [...state.Certificates, Certificate],
        CurrentCertificate: Certificate,
    })),
    on(CertificateActions.deleteCertificate, (state) => deleteFlags(state)),
    on(CertificateActions.deleteCertificateSuccess, (state, { Certificate }) => ({
        ...state,
        Loading: false,
        Message: "Certificado eliminado correctamente...",
        Certificates: [...state.Certificates],
        CurrentCertificate: null,
    })),
    on(CertificateActions.selectCertificate, (state, { Certificate }) => ({
        ...state,
        CurrentCertificate: Certificate
    })),
    on(CertificateActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
        };
    }),

    //---------------------------Query-----------------------------------------
    //--------------------------------------------------------------------------
    on(QueryActions.searchQueries, (state) => searchFlags(state)),
    on(QueryActions.searchQueriesSuccess, (state, { Queries }) => ({ ...state, Loading: false, Queries })),
    on(QueryActions.searchQueryOne, (state) => searchFlags(state)),
    on(QueryActions.searchQueryOneSuccess, (state, { QueryOne }) => ({ ...state, Loading: false, QueryOne })),
    on(QueryActions.createQuery, (state) => updateFlags(state)),
    on(QueryActions.createQuerySuccess, (state, { Query }) => ({
        ...state,
        Loading: false,
        Message: "Consulta creada correctamente...",
        Queries: [...state.Queries, Query],
        CurrentQuery: null,
    })),
    on(QueryActions.updateQuery, (state) => updateFlags(state)),
    on(QueryActions.updateQuerySuccess, (state, { Query }) => ({
        ...state,
        Loading: false,
        Message: "Consulta actualizada correctamente...",
        Queries: [...state.Queries, Query],
        CurrentQuery: Query,
    })),
    on(QueryActions.deleteQuery, (state) => deleteFlags(state)),
    on(QueryActions.deleteQuerySuccess, (state, { Query }) => ({
        ...state,
        Loading: false,
        Message: "Consulta eliminada correctamente...",
        Queries: [...state.Queries],
        CurrentQuery: null,
    })),
    on(QueryActions.selectQuery, (state, { Query }) => ({
        ...state,
        CurrentQuery: Query
    })),
    on(QueryActions.resetLoading, state => {
        return {
            ...state,
            Loading: false,
        };
    }),

    on(QueryActions.cleanQuery, (state, { QueryOne }) => ({ ...state, QueryOne: null, CertificatesFile: [] })),

    on(ConfigurationActions.configurationError, (state, { error }) => ({ ...state, Error:error, Loading: false })),
);

const searchFlags = (state: State): State => {
    return {
        ...state,
        Error: null,
        Message: null,
        Loading: true,
        LoadingPolicy: true,
        LoadingCustomer: true,
        LoadingArea: true,
    }
}
const updateFlags = (state: State): State => {
    return {
        ...state,
        Error: null,
        Loading: true,
        Message: null
    }
}
const deleteFlags = (state: State): State => {
    return {
        ...state,
        Error: null,
        Loading: true,
        Message: null
    }
}
export function reducer(state: State | undefined, action: Action) {
    return configurationReducer(state, action);
}

export const getCustomers = (state: State) => state.Customers;
export const getCustomersAuthorization = (state: State) => state.CustomersAuthorization;
export const getCurrentCustomer = (state: State) => state.CurrentCustomer;
export const getLoadingCustomer = (state: State) => state.LoadingCustomer;

export const getPolicies = (state: State) => state.Policies;
export const getCurrentPolicy = (state: State) => state.CurrentPolicy;
export const getLoadingPolicy = (state: State) => state.LoadingPolicy

export const getCertificates= (state: State) => state.Certificates;
export const getCertificateStatus= (state: State) => state.CertificateStatus;
export const getCertificatesByState= (state: State) => state.CertificatesByState;
export const getCertificatesFile= (state: State) => state.CertificatesFile;
export const getCurrentCertificate = (state: State) => state.CurrentCertificate;

export const getCertificateHistoricals= (state: State) => state.CertificateHistoricals;
export const getCurrentCertificateHistorical = (state: State) => state.CurrentCertificateHistorical;

export const getCertificateTypes= (state: State) => state.CertificateTypes;
export const getCurrentCertificateType = (state: State) => state.CurrentCertificateType;

export const getRoles= (state: State) => state.Roles;
export const getCurrentRole = (state: State) => state.CurrentRole;

export const getAreas= (state: State) => state.Areas;
export const getCurrentArea = (state: State) => state.CurrentArea;
export const getLoadingArea = (state: State) => state.LoadingArea;

export const getGeneralConfigs= (state: State) => state.GeneralConfigs;
export const getCurrentGeneralConfig = (state: State) => state.CurrentGeneralConfig;
export const getLoadingGeneralConfig = (state: State) => state.LoadingGeneralConfig;

export const getQueries = (state: State) => state.Queries;
export const getCurrentQuery = (state: State) => state.CurrentQuery;
export const getQueryOne = (state: State) => state.QueryOne;

export const getError = (state: State) => state.Error;
export const getLoading = (state: State) => state.Loading;
export const getMessageAction = (state: State) => state.Message;
